import { useCallback, useContext } from 'react';
import clsx from 'clsx';
import { AText, Box } from '@alf/uikit';
import { FORM_COMPONENTS_TYPES } from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import { Icon } from '@namespace/icons2';
import { useNotifications } from '@namespace/notifications';
import { UserContext } from '@namespace/user';
import { sendVerificationEmail } from '../../../api/emailConfirmation';

import styles from '../index.module.css';

const useEmailFormComponent = ({ classNames = {}, buttonProps, hintProps }) => {
  const { t } = useI18n();
  const [user] = useContext(UserContext);
  const { successNotification, errorNotification } = useNotifications();

  const { inputWrapClassName, buttonClassName } = classNames;

  const { accountStatuses } = user;
  const { isEmailVerified } = accountStatuses;

  const onEmailVerify = useCallback(() => {
    sendVerificationEmail()
      .then(() =>
        successNotification(
          t('profile.data.verificationEmail.successNotification')
        )
      )
      .catch(() =>
        errorNotification(t('profile.data.verificationEmail.errorNotification'))
      );
  }, [successNotification, errorNotification, t]);

  const {
    iconNameStateVerified = 'icons/general/state/success',
    iconNameStateNotVerified = 'icons/general/state/warning',
    iconColorStateNotVerified = 'state_warning',
    iconColorStateVerified = 'state_success',
    iconClassName = '',
    textColor = 'po_txt_color_3',
    hintClassName = ''
  } = hintProps;

  const emailVerificationHint = (
    <Box>
      <div className={clsx(styles.verificationIconContainer, iconClassName)}>
        <Icon
          name={
            isEmailVerified ? iconNameStateVerified : iconNameStateNotVerified
          }
          size="xs"
          color={
            isEmailVerified ? iconColorStateVerified : iconColorStateNotVerified
          }
        />
      </div>
      <AText size="s" color={textColor}>
        {t(
          `personalOffice.profile.data.${
            isEmailVerified ? 'emailVerified' : 'emailUnverified'
          }`
        )}
      </AText>
    </Box>
  );

  const emailVerificationField = {
    type: FORM_COMPONENTS_TYPES.VERIFICATION_FIELD,
    name: 'email',
    label: 'personalOffice.profile.data.email',
    buttonText: 'personalOffice.profile.data.emailVerifyButton',
    hint: emailVerificationHint,
    onVerifyClick: onEmailVerify,
    isDisplayVerificationButton: !isEmailVerified,
    classNames: {
      wrapperClassName: clsx([inputWrapClassName, styles.inputWrap]),
      buttonClassName,
      hintClassName
    },
    buttonProps,
    disabled: true,
    dataRole: 'email-verification'
  };

  return { element: emailVerificationField };
};

export default useEmailFormComponent;
